import { axios } from '@/plugins/http-axios';

// Устанавливаем заголовок Authorization при инициализации, если токен есть
const initialToken = localStorage.getItem('token');
if (initialToken) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${initialToken}`;
}

const state = {
  user: null,
  token: initialToken,
  isAuthenticated: !!initialToken
};

const mutations = {
  SET_TOKEN(state, token) {
    state.token = token;
    state.isAuthenticated = !!token;
    if (token) {
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    }
  },
  
  SET_USER(state, user) {
    state.user = user;
  },
  
  LOGOUT(state) {
    state.user = null;
    state.token = null;
    state.isAuthenticated = false;
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
  }
};

const actions = {
  async login({ commit, dispatch }, { phone, password }) {
    try {
      const response = await axios.post('/auth/login', {
        phone,
        password
      });
      
      const { token, user } = response.data;
      commit('SET_TOKEN', token);
      commit('SET_USER', user);
      
      // Очищаем старое состояние loyalty и загружаем новое
      dispatch('loyalty/reset', null, { root: true });
      
      return { success: true };
    } catch (error) {
      // Бэкенд отдаёт `{ errorCode, message }` (см. ApiException + GlobalExceptionHandler).
      // Различаем USER_NOT_FOUND (нет такого пользователя) и INVALID_PASSWORD (неверный пароль).
      const data = error.response?.data || {};
      const errorCode = data.errorCode || null;
      const errorMessage = data.message || data.error || error.message || 'Ошибка входа';
      return {
        success: false,
        error: errorMessage,
        errorCode,
        userNotFound: errorCode === 'USER_NOT_FOUND',
        invalidPassword: errorCode === 'INVALID_PASSWORD'
      };
    }
  },
  
  async register({ commit, dispatch }, { phone, password, firstName, lastName, email, birthday, referralCode }) {
    try {
      const response = await axios.post('/auth/register', {
        phone,
        password,
        firstName,
        lastName,
        email,
        birthday: birthday || null,
        referralCode: referralCode || null
      });
      
      const { token, user } = response.data;
      commit('SET_TOKEN', token);
      commit('SET_USER', user);
      
      // Очищаем старое состояние loyalty и загружаем новое
      dispatch('loyalty/reset', null, { root: true });
      
      return { success: true };
    } catch (error) {
      const data = error.response?.data || {};
      return {
        success: false,
        error: data.message || data.error || 'Ошибка регистрации',
        errorCode: data.errorCode || null
      };
    }
  },
  
  async getProfile({ commit, state }) {
    if (!state.token) return;
    
    try {
      const response = await axios.get('/auth/profile');
      // Java backend возвращает UserDto напрямую, а не обернутый в { user: ... }
      commit('SET_USER', response.data);
    } catch (error) {
      // Логаутим только при 401/403 (токен невалиден), не при сетевых ошибках
      const status = error.response?.status;
      if (status === 401 || status === 403) {
        commit('LOGOUT');
      }
      // При других ошибках (сеть, 500) — оставляем токен, пользователь может повторить
    }
  },
  
  async updateProfile({ commit }, profileData) {
    try {
      const response = await axios.put('/auth/profile', profileData);
      commit('SET_USER', response.data);
      return { success: true };
    } catch (error) {
      const data = error.response?.data || {};
      return {
        success: false,
        error: data.message || data.error || 'Ошибка обновления профиля',
        errorCode: data.errorCode || null
      };
    }
  },
  
  async changePassword({ commit }, { currentPassword, newPassword }) {
    try {
      await axios.put('/auth/change-password', {
        currentPassword,
        newPassword
      });
      return { success: true };
    } catch (error) {
      const data = error.response?.data || {};
      return {
        success: false,
        error: data.message || data.error || 'Ошибка смены пароля',
        errorCode: data.errorCode || null
      };
    }
  },
  
  async cancelRecurring({ commit }) {
    try {
      const response = await axios.put('/auth/cancel-recurring');
      commit('SET_USER', response.data);
      return { success: true };
    } catch (error) {
      const data = error.response?.data || {};
      return {
        success: false,
        error: data.message || data.error || 'Ошибка отмены подписки',
        errorCode: data.errorCode || null
      };
    }
  },
  
  logout({ commit, dispatch }) {
    commit('LOGOUT');
    // Очищаем состояние loyalty при выходе
    dispatch('loyalty/reset', null, { root: true });
  },
  
  async initializeAuth({ commit, dispatch }) {
    const token = localStorage.getItem('token');
    if (token) {
      commit('SET_TOKEN', token);
      // Загружаем профиль пользователя после установки токена
      // Логаут произойдёт только при 401/403 (внутри getProfile)
      await dispatch('getProfile');
    }
  }
};

const getters = {
  isAuthenticated: state => state.isAuthenticated,
  user: state => state.user,
  getUser: state => state.user,
  isAdmin: state => state.user && state.user.role === 'ADMIN',
  isApplicationManager: state => state.user && state.user.role === 'BUSINESS_MANAGER',
  canManageApplications: state => state.user && ['ADMIN', 'BUSINESS_MANAGER'].includes(state.user.role),
  isCashier: state => state.user && state.user.role === 'CASHIER',
  isTablet: state => state.user && state.user.role === 'QR_TABLET',
  isStaff: state => state.user && (state.user.role === 'ADMIN' || state.user.role === 'CASHIER'),
  canShowTablet: state => state.user && (
      state.user.role === 'ADMIN' || state.user.role === 'CASHIER' || state.user.role === 'QR_TABLET'
  ),
  hasRecurringDonation: state => state.user?.recurringDonation?.isActive || false
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
